const API_BASE_URL = process.env.REACT_APP_API_URL;

// Helper function to build complete image URLs
const buildImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // If already a complete URL, return as is
  if (imageUrl.startsWith('http')) {
    return imageUrl;
  }
  
  // Get the API URL from environment
  const apiUrl = process.env.REACT_APP_API_URL;
  
  // For relative URLs, construct the base URL dynamically
  let baseUrl;
  if (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')) {
    // For localhost, remove /api and use as base
    baseUrl = apiUrl.replace('/api', '');
  } else {
    // For production, remove /api and use as base  
    baseUrl = apiUrl.replace('/api', '');
  }
  
  const fullUrl = `${baseUrl}${imageUrl}`;
  
  return fullUrl;
};

export const productService = {
  // Fetch complete product details with all required data
  getProductDetails: async (productId = null) => {
    try {
      // Use the populate parameters that work with your Strapi schema
      const apiUrl = `${API_BASE_URL}/products?` + [
        'populate[keyFeatures][populate][features][populate]=*',
        'populate[idealFor][populate][features][populate]=*',
        'populate[galleryImages][populate]=images',
        'populate[productHeader][populate]=headerImage',
        'populate[statistics][populate]=image',
        'populate[exploreSection]=true',
        'populate[mission]=true',
        'populate[vision]=true',
        'populate=productImage'
      ].join('&');
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.data && result.data.length > 0) {
        // Take first product or find by ID if specified
        const productData = productId 
          ? result.data.find(p => p.id === parseInt(productId))
          : result.data[0];
          
        if (!productData) {
          throw new Error('Product not found');
        }
        
        // Format the data using the existing formatting function
        const formattedProduct = formatProductData(productData);
       
        return {
          success: true,
          data: formattedProduct
        };
      } else {
        throw new Error('No product data found');
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Fetch single product data with full population (for carousel/details)
  getProductData: async () => {
    try {
      // First, get basic product data with all fields including mission and vision
      const basicResponse = await fetch(`${API_BASE_URL}/products?populate[keyFeatures][populate][features][populate]=*&populate[idealFor][populate][features][populate]=*&populate[galleryImages][populate]=images&populate[productHeader][populate]=headerImage&populate[statistics][populate]=image&populate[mission]=true&populate[vision]=true&populate=productImage`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!basicResponse.ok) {
        throw new Error(`HTTP error! status: ${basicResponse.status}`);
      }
      
      const basicResult = await basicResponse.json();
      
      // Then, get header images specifically
      const headerResponse = await fetch(`${API_BASE_URL}/products?populate[productHeader][populate][0]=headerImage`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let headerResult = null;
      if (headerResponse.ok) {
        headerResult = await headerResponse.json();
      }

      // Get gallery images specifically
      const galleryResponse = await fetch(`${API_BASE_URL}/products?populate[galleryImages][populate][0]=images`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let galleryResult = null;
      if (galleryResponse.ok) {
        galleryResult = await galleryResponse.json();
      }

      // Get statistics with icons specifically
      const statisticsResponse = await fetch(`${API_BASE_URL}/products?populate[statistics][populate][0]=image`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let statisticsResult = null;
      if (statisticsResponse.ok) {
        statisticsResult = await statisticsResponse.json();
      }

      // Get keyFeatures specifically
      const keyFeaturesResponse = await fetch(`${API_BASE_URL}/products?populate[keyFeatures][populate][features][populate]=*`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let keyFeaturesResult = null;
      if (keyFeaturesResponse.ok) {
        keyFeaturesResult = await keyFeaturesResponse.json();
      }

      // Get idealFor specifically
      const idealForResponse = await fetch(`${API_BASE_URL}/products?populate[idealFor][populate]=features`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let idealForResult = null;
      if (idealForResponse.ok) {
        idealForResult = await idealForResponse.json();
      }

      // Get exploreSection specifically
      const exploreSectionResponse = await fetch(`${API_BASE_URL}/products?populate[exploreSection]=true`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let exploreSectionResult = null;
      if (exploreSectionResponse.ok) {
        exploreSectionResult = await exploreSectionResponse.json();
      }
      
      // Merge the data - add headerImage and galleryImages to basic data
      if (basicResult.data) {
        basicResult.data.forEach((basicProduct, index) => {
          // Merge header images
          if (headerResult?.data) {
            const headerProduct = headerResult.data.find(hp => hp.id === basicProduct.id);
            if (headerProduct?.productHeader?.headerImage) {
              basicProduct.productHeader = {
                ...basicProduct.productHeader,
                headerImage: headerProduct.productHeader.headerImage
              };
            }
          }

          // Merge gallery images
          if (galleryResult?.data) {
            const galleryProduct = galleryResult.data.find(gp => gp.id === basicProduct.id);
            if (galleryProduct?.galleryImages) {
              basicProduct.galleryImages = galleryProduct.galleryImages;
            }
          }

          // Merge statistics with icons
          if (statisticsResult?.data) {
            const statisticsProduct = statisticsResult.data.find(sp => sp.id === basicProduct.id);
            if (statisticsProduct?.statistics) {
              basicProduct.statistics = statisticsProduct.statistics;
            }
          }

          // Merge keyFeatures
          if (keyFeaturesResult?.data) {
            const keyFeaturesProduct = keyFeaturesResult.data.find(kf => kf.id === basicProduct.id);
            if (keyFeaturesProduct?.keyFeatures) {
              basicProduct.keyFeatures = keyFeaturesProduct.keyFeatures;
            }
          }

          // Merge idealFor
          if (idealForResult?.data) {
            const idealForProduct = idealForResult.data.find(idf => idf.id === basicProduct.id);
            if (idealForProduct?.idealFor) {
              basicProduct.idealFor = idealForProduct.idealFor;
            }
          }

          // Merge exploreSection
          if (exploreSectionResult?.data) {
            const exploreSectionProduct = exploreSectionResult.data.find(esp => esp.id === basicProduct.id);
            if (exploreSectionProduct?.exploreSection) {
              basicProduct.exploreSection = exploreSectionProduct.exploreSection;
            }
          }
        });
      }
      
      const result = basicResult;
      
      // For collection types, data.data is an array, so take the first item
      const productData = result.data && result.data.length > 0 ? result.data[0] : null;
      
      if (!productData) {
        return {
          success: false,
          error: 'No product data found'
        };
      }
      
      // Fetch gallery images separately with proper population
      let galleryImages = [];
      try {
        const galleryResponse = await fetch(`${API_BASE_URL}/products?populate=galleryImages`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          mode: 'cors',
          credentials: 'omit'
        });
        if (galleryResponse.ok) {
          const galleryResult = await galleryResponse.json();
          const firstProduct = galleryResult.data && galleryResult.data.length > 0 ? galleryResult.data[0] : null;
          galleryImages = firstProduct?.galleryImages || [];
        }
      } catch (galleryError) {
        console.warn('Could not fetch gallery images:', galleryError);
      }
      
      return {
        success: true,
        data: {
          ...productData,
          galleryImages: galleryImages
        }
      };
    } catch (error) {
      console.error('Error fetching product data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Fetch all products for megamenu
  getAllProducts: async () => {
    try {
      // First, get basic product data with all fields
      const basicResponse = await fetch(`${API_BASE_URL}/products?populate=*`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!basicResponse.ok) {
        throw new Error(`HTTP error! status: ${basicResponse.status}`);
      }
      
      const basicResult = await basicResponse.json();
      
      // Then, get header images specifically
      const headerResponse = await fetch(`${API_BASE_URL}/products?populate[productHeader][populate][0]=headerImage`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let headerResult = null;
      if (headerResponse.ok) {
        headerResult = await headerResponse.json();
      }

      // Get gallery images specifically
      const galleryResponse = await fetch(`${API_BASE_URL}/products?populate[galleryImages][populate][0]=images`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let galleryResult = null;
      if (galleryResponse.ok) {
        galleryResult = await galleryResponse.json();
      }

      // Get statistics with icons specifically
      const statisticsResponse = await fetch(`${API_BASE_URL}/products?populate[statistics][populate][0]=image`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit'
      });
      
      let statisticsResult = null;
      if (statisticsResponse.ok) {
        statisticsResult = await statisticsResponse.json();
      }
      
      // Merge the data - add headerImage, galleryImages and statistics to basic data
      if (basicResult.data) {
        basicResult.data.forEach((basicProduct, index) => {
          // Merge header images
          if (headerResult?.data) {
            const headerProduct = headerResult.data.find(hp => hp.id === basicProduct.id);
            if (headerProduct?.productHeader?.headerImage) {
              basicProduct.productHeader = {
                ...basicProduct.productHeader,
                headerImage: headerProduct.productHeader.headerImage
              };
            }
          }

          // Merge gallery images
          if (galleryResult?.data) {
            const galleryProduct = galleryResult.data.find(gp => gp.id === basicProduct.id);
            if (galleryProduct?.galleryImages) {
              basicProduct.galleryImages = galleryProduct.galleryImages;
            }
          }

          // Merge statistics with icons
          if (statisticsResult?.data) {
            const statisticsProduct = statisticsResult.data.find(sp => sp.id === basicProduct.id);
            if (statisticsProduct?.statistics) {
              basicProduct.statistics = statisticsProduct.statistics;
            }
          }
        });
      }
      
      // Return all products for megamenu
      const productsData = basicResult.data || [];
      
      return {
        success: true,
        data: productsData
      };
    } catch (error) {
      console.error('Error fetching all products:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

// Helper function to format multiple products for the megamenu
export const formatProductsForMegaMenu = (apiDataArray) => {
  if (!apiDataArray || !Array.isArray(apiDataArray)) return [];

  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '');
  
  return apiDataArray.map((apiData, index) => ({
    id: apiData.id,
    title: apiData.productHeader?.title || 
           (apiData.title ? apiData.title.replace(/\n\nAt a Glance.*$/i, '').trim() : 'Product'),
    description: apiData.description || 'No description available',
    // Use productLogo for megamenu display, fallback to productImage if no logo
    image: apiData.productLogo?.url 
      ? `${baseUrl}${apiData.productLogo.url}`
      : apiData.productImage?.url 
      ? `${baseUrl}${apiData.productImage.url}`
      : null,
    badge: apiData.statistics?.[0]?.title?.replace(/\n/g, '') || null,
    documentId: apiData.documentId || apiData.id
  }));
};

// Helper function to format API data for the carousel (single product)
export const formatProductData = (apiData) => {
  if (!apiData) return null;
  
  return {
    id: apiData.id,
    // Clean up the title to remove extra text like "\n\nAt a Glance"
    title: apiData.productHeader?.title || 
           (apiData.title ? apiData.title.replace(/\n\nAt a Glance.*$/i, '').trim() : 'Product'),
    // Use the main description field for AtAGlance component
    description: apiData.description || 'No description available',
    detailedDescription: apiData.description || 'No detailed description available',
    logoText: apiData.title?.split(' ')[0]?.toUpperCase() || 'PRODUCT',
    badgeText: apiData.statistics?.[0]?.title?.replace(/\n/g, '') || 'Featured',
    logoGradient: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
    screenBg: "purple.500",
    ticketBg: "purple.400",
    badgeBg: "purple.400",
    circularBorder: "purple.400",
    rotation: "rotate(-2deg)",
    
    // Add productHeader data
    productHeader: {
      title: apiData.productHeader?.title,
      description: apiData.productHeader?.description,
      headerImage: buildImageUrl(apiData.productHeader?.headerImage?.url)
    },
    
    // General image handling - if image exists, build URL; if not, null
    productImage: buildImageUrl(apiData.productImage?.url),
    productLogo: buildImageUrl(apiData.productLogo?.url) || buildImageUrl(apiData.productImage?.formats?.thumbnail?.url),
    imageUrl: buildImageUrl(apiData.productImage?.formats?.thumbnail?.url),
    detailImage: buildImageUrl(apiData.productImage?.url),
    headerImage: buildImageUrl(apiData.productHeader?.headerImage?.url),
    
    // Mission and Vision from API
    mission: apiData.mission?.description || null,
    vision: apiData.vision?.description || null,
    
    // Debug logging for mission/vision
    ...(console.log("Raw API mission data:", apiData.mission), {}),
    ...(console.log("Raw API vision data:", apiData.vision), {}),
    ...(console.log("Processed mission:", apiData.mission?.description || null), {}),
    ...(console.log("Processed vision:", apiData.vision?.description || null), {}),
    // Transform statistics for the detail page
    stats: apiData.statistics?.map((stat, index) => {      
      return {
        iconImage: buildImageUrl(stat.image?.url),
        number: stat.title?.replace(/\n/g, '') || '',
        label: stat.description || ''
      };
    }) || [],
    // Transform keyFeatures - use the actual API structure with description
    features: apiData.keyFeatures?.features?.map(feature => ({
      title: feature.title,
      description: feature.description,
      icon: feature.icon ? buildImageUrl(feature.icon.url) : null
    })) || [],
    
    // Transform keyFeatures object - pass the full features array with all fields
    keyFeatures: apiData.keyFeatures?.features?.map(feature => ({
      title: feature.title,
      description: feature.description,
      icon: feature.icon ? buildImageUrl(feature.icon.url) : null
    })) || [],
    // Transform idealFor - use the actual API structure with description
    idealFor: apiData.idealFor?.features?.map(feature => ({
      title: feature.title,
      description: feature.description,
      icon: feature.icon ? buildImageUrl(feature.icon.url) : null
    })) || [],
    keyHighlights: [], // Field doesn't exist in current backend structure
    exploreSection: apiData.exploreSection,
    
    // Transform gallery images for display
    galleryImages: apiData.galleryImages?.map((galleryItem, index) => {
      const imageUrl = galleryItem.images?.url;
      return {
        id: galleryItem.id,
        src: buildImageUrl(imageUrl),
        alt: galleryItem.images?.alternativeText || galleryItem.images?.name || `Gallery Image ${index + 1}`,
        caption: galleryItem.images?.caption || '',
        // Define grid layout for images
        colSpan: index === 0 ? { base: 1, md: 3 } : { base: 1, md: 2 },
        rowSpan: index === 0 ? { base: 1, md: 2 } : 1,
        formats: galleryItem.images?.formats ? Object.keys(galleryItem.images.formats).reduce((acc, key) => {
          acc[key] = {
            ...galleryItem.images.formats[key],
            url: buildImageUrl(galleryItem.images.formats[key].url)
          };
          return acc;
        }, {}) : {}
      };
    }) || []
  };
};

// Helper function to format API data for mega menu
export const formatProductForMegaMenu = (apiData) => {
  if (!apiData) return null;
  
  return {
    id: apiData.id,
    title: apiData.productHeader?.title || apiData.title || null,
    description: apiData.description || null,
    // For mega menu, use the main product image or thumbnail
    image: buildImageUrl(apiData.productImage?.formats?.small?.url) || buildImageUrl(apiData.productImage?.url),
    badge: apiData.statistics?.[0]?.title?.replace(/\n/g, '') || null
  };
};
